import { MoveDownOutlined, SearchRounded } from '@mui/icons-material';
import { Button, Stack, TextField, debounce } from '@mui/material';
import React, { memo, useMemo, useState } from 'react';
import { getColor } from '../../../../../assets/theme';
//import ServerError from '../../../../../components/animation/server-error';
import DataTable from '../../../../../components/table/datatable';

import ContainerTitle from '@components/ui/container-title';
import { LoadingButton } from '@mui/lab';
import { GridColDef, GridFilterModel, useGridApiRef } from '@mui/x-data-grid';
import {
  useGetInHouseGuestQuery,
  usePostLineTransferMutation,
} from '../../../../../api/front-office/roomposting.api.rtk';
import useInformationDialog from '../../../../../hooks/useInformationDialog';
import useToast from '../../../../../hooks/useToast';
import { useRoomPostingContext } from '../../../containers/fo-room_posting.container';
import { RoomPostingLineTransfer } from '../../../interface/room-posting-interface';

const LineTransferColumns: GridColDef[] = [
  {
    field: 'reference_id',
    headerName: 'Reference',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'booking_id',
    headerName: 'Res ID',
    minWidth: 105,
    flex: 1,
  },
  {
    field: 'room_number',
    headerName: 'Room Number',
    minWidth: 105,
    flex: 1,
  },
  {
    field: 'room_code',
    headerName: 'Room Type',
    minWidth: 105,
    flex: 1,
  },
  {
    field: 'guest_name',
    headerName: 'Guest Name',
    minWidth: 200,
    flex: 1,
  },
];

const FORoomPostingLineTransferForm = ({
  handleClose,
  selectedIDs,
}: {
  handleClose: () => void;
  selectedIDs: string;
}) => {
  const { primary } = getColor();
  const apiRef = useGridApiRef();
  const { toast } = useToast();

  const { DialogInformation, onShowInfoDialog } = useInformationDialog();

  const { roomPostingLedgerList, useProperty, ledgerRefetch } =
    useRoomPostingContext();

  const roomNo = useMemo(
    () => roomPostingLedgerList?.data?.roomNo,
    [roomPostingLedgerList?.data?.roomNo],
  );

  const {
    data: inHouseListData = [],
    isFetching,
    isLoading,
  } = useGetInHouseGuestQuery(roomNo, {
    refetchOnMountOrArgChange: true,
  });

  const [postTransferLine, { isLoading: isProcessing }] =
    usePostLineTransferMutation();

  const [selRoomNo, setSelRoomNo] = useState('');
  const [selRefID, setSelRefID] = useState('');

  const handleConfirm = async () => {
    if (selRefID.length === 0) {
      onShowInfoDialog({
        desc: 'Please select room to line transfer.',
      });

      return;
    }

    const postLineTransfer: RoomPostingLineTransfer = {
      properties: [
        {
          property: {
            propertyID: useProperty,
            line_transfer_list: [
              {
                identifier_list: selectedIDs,
                ref_id: selRefID,
              },
            ],
          },
        },
      ],
    };

    await postTransferLine(postLineTransfer)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: `Successfully transferred to Room:${selRoomNo}.`,
        });
        handleClose();
        ledgerRefetch();
      })
      .catch((error) =>
        toast({
          severity: 'error',
          message: `Error occurred during line transfer: ${error}`,
        }),
      );
  };

  const updateSearchValue = useMemo(() => {
    return debounce((newValue) => {
      const newFilterModel: GridFilterModel = {
        items: [],
        quickFilterValues: [newValue],
      };
      apiRef?.current?.setFilterModel(newFilterModel);
    }, 500);
  }, [apiRef?.current]);

  const handleSearchFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.value;
    updateSearchValue(newValue);
  };

  return (
    <Stack gap={2} sx={{ p: 3, width: '100%' }}>
      <Stack>
        <Stack pb={1}>
          <Stack
            sx={{
              color: primary,
              backgroundColor: '#f7f7fb',
              width: '60px',
              height: '60px',
              borderRadius: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <MoveDownOutlined />
          </Stack>

          <ContainerTitle
            title='Line Transfer'
            func={() => {}}
            titleStyle={{
              fontSize: '18px!important',
              fontWeight: '500',
            }}
          />
        </Stack>
        <TextField
          id='outlined-basic'
          label='Search by reservation ID,room name, number, or folio.'
          type='search'
          variant='outlined'
          size='small'
          sx={{
            width: '100%',
          }}
          autoComplete='off'
          onChange={handleSearchFilterChange}
          InputProps={{
            startAdornment: <SearchRounded />,
          }}
        />
        <DataTable
          height={339}
          getRowId={(row) => row.reference_id}
          columns={LineTransferColumns}
          rows={inHouseListData?.data || []}
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableSearch
          checkboxSelection={false}
          pageSize={50}
          apiRef={apiRef}
          initialState={{
            columns: {
              columnVisibilityModel: {
                reference_id: false,
              },
            },
          }}
          loading={isFetching || isLoading}
          onRowClick={(e) => {
            setSelRefID(e.row.reference_id);
            setSelRoomNo(e.row.room_number);
          }}
          onRowDoubleClick={handleConfirm}
          sx={{ p: 0 }}
        />
      </Stack>
      <Stack alignItems='flex-end'>
        <Stack direction='row' gap={1}>
          <Button
            variant='outlined'
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            onClick={handleConfirm}
            disabled={isProcessing}
            loading={isProcessing}
          >
            Confirm
          </LoadingButton>
        </Stack>
      </Stack>
      <DialogInformation />
    </Stack>
  );
};

export default memo(FORoomPostingLineTransferForm);
